import { Map } from 'immutable'
import { AsyncState, HttpResult, loadingAsyncState, mk_pair, Pair } from 'widgets-for-react'
import * as UriJS from 'uri-js'
import { ParsedFileImage, ParsedParagraphMediaNested } from '../types_and_parser/types'
import { ImageSize, Url } from '../../cms/blocks/types.gen'
import { Block } from '../types_and_parser/standard_blocks.gen'
import { PairAsyncImage } from '../../cms/state/types.gen'
import { ExtraImages } from '../../public_site/utils/extra_images.mod'
import { isCustomBlock } from '../../public_site/blocks/custom_blocks/types.mod'
import { CustomBlock } from '../types_and_parser/custom_blocks.mod'

export const parseImageUrl = (s: string): string => s == undefined || s == null ? '' :
  `/drupal/media${UriJS.parse(s).path}${UriJS.parse(s).query ? '?' + UriJS.parse(s).query : ''}`

export function loadMedia(image: ParsedFileImage): AsyncState<Map<ImageSize<ExtraImages>, Url>> {
  return loadingAsyncState<Map<ImageSize<ExtraImages>, Url>>(async (_) => {
    if (image == null) {
      return {
        kind: 'result',
        status: 400,
        value: null
      }
    }
    const sizes = image.style_urls
    let res = Map<ImageSize<ExtraImages>, Url>().set('full', parseImageUrl(image.url))

    for (const prop in sizes) {
      if (sizes[prop] == undefined) {
        console.warn(`Error: missing image url. ${prop} is <undefined> for image with URL ${image.url}`)
      }
    }

    if (sizes.thumbnail) res = res.set('thumbnail', parseImageUrl(sizes.thumbnail))
    if (sizes.grid) { res = res.set('grid', parseImageUrl(sizes.grid)) }
    if (sizes.media) { res = res.set('media', parseImageUrl(sizes.media)) }
    if (sizes.large) res = res.set('large', parseImageUrl(sizes.large))
    if (sizes.medium) res = res.set('medium', parseImageUrl(sizes.medium))
    if (sizes.crop_thumbnail) res = res.set('crop_thumbnail', parseImageUrl(sizes.crop_thumbnail))

    if (sizes['720x470']) res = res.set('720x470', parseImageUrl(sizes['720x470']))
    if (sizes['640x360']) res = res.set('640x360', parseImageUrl(sizes['640x360']))
    if (sizes['1280x720']) res = res.set('1280x720', parseImageUrl(sizes['1280x720']))

    if (sizes['450x450']) res = res.set('450x450', parseImageUrl(sizes['450x450']))
    if (sizes['600x600']) res = res.set('600x600', parseImageUrl(sizes['600x600']))
    if (sizes['1200x1200']) res = res.set('1200x1200', parseImageUrl(sizes['1200x1200']))

    if (sizes['700w']) res = res.set('700w', parseImageUrl(sizes['700w']))
    if (sizes['1400w']) res = res.set('1400w', parseImageUrl(sizes['1400w']))
    if (sizes['2800w']) res = res.set('2800w', parseImageUrl(sizes['2800w']))

    if (sizes['425w']) res = res.set('425w', parseImageUrl(sizes['425w']))
    if (sizes['850w']) res = res.set('850w', parseImageUrl(sizes['850w']))
    if (sizes['1225w']) res = res.set('1225w', parseImageUrl(sizes['1225w']))

    if (sizes['640w']) res = res.set('640w', parseImageUrl(sizes['640w']))
    if (sizes['720w']) res = res.set('720w', parseImageUrl(sizes['720w']))
    if (sizes['1280w']) res = res.set('1280w', parseImageUrl(sizes['1280w']))
    if (sizes['1920w']) res = res.set('1920w', parseImageUrl(sizes['1920w']))
    if (sizes['2880w']) res = res.set('2880w', parseImageUrl(sizes['2880w']))

    return {
      kind: 'result',
      status: 200,
      value: res
    }
  })
}

export const GetDrupalBlockImagesLoaders = (
  block: Block | CustomBlock,
  get_images_from_custom_block?: (b: CustomBlock) => Array<PairAsyncImage<ExtraImages>>
): Array<Pair<number, AsyncState<Map<ImageSize<ExtraImages>, Url>>>> => {
  switch (block.kind) {
    case 'featured_content':
      return [
        mk_pair(
          block.field_image &&
            block.field_image.bundle === 'image' &&
            block.field_image.field_media_image &&
            block.field_image.field_media_image.image.id
            ? block.field_image.field_media_image.image.id
            : null,
          loadMedia(
            block.field_image &&
              block.field_image.bundle === 'image' &&
              block.field_image.field_media_image &&
              block.field_image.field_media_image.image.id
              ? block.field_image.field_media_image.image
              : null
          ) as AsyncState<Map<ImageSize<ExtraImages>, Url>>
        )
      ]
    case 'text_with_image':
      return [
        mk_pair(
          block.field_image &&
            block.field_image.bundle === 'image' &&
            block.field_image.field_media_image &&
            block.field_image.field_media_image.image.id
            ? block.field_image.field_media_image.image.id
            : null,
          loadMedia(
            block.field_image &&
              block.field_image.bundle === 'image' &&
              block.field_image.field_media_image &&
              block.field_image.field_media_image.image.id
              ? block.field_image.field_media_image.image
              : null
          ) as AsyncState<Map<ImageSize<ExtraImages>, Url>>
        )
      ]
    case 'grid_blocks_image':
      return block.field_grid_blocks
        .map((b) =>
          mk_pair(
            b.field_image.field_media_image.image.id,
            loadMedia(b.field_image.field_media_image.image) as AsyncState<Map<ImageSize<ExtraImages>, Url>>
          )
        )
        .toArray()
    case 'image_gallery':
      return block.field_image_gallery
        .map((b) =>
          mk_pair(
            b.field_image.field_media_image.image.id,
            loadMedia(b.field_image.field_media_image.image) as AsyncState<Map<ImageSize<ExtraImages>, Url>>
          )
        )
        .toArray()
    case 'media':
      return [
        mk_pair(
          block.field_media.bundle === 'image' ? block.field_media.field_media_image.image.id : null,
          loadMedia(block.field_media.bundle === 'image' ? block.field_media.field_media_image.image : null) as AsyncState<Map<ImageSize<ExtraImages>, Url>>
        )
      ]
    case 'cta':
      return [
        mk_pair(
          block.field_image &&
            block.field_image.bundle === 'image' &&
            block.field_image.field_media_image &&
            block.field_image.field_media_image.image.id
            ? block.field_image.field_media_image.image.id
            : null,
          loadMedia(
            block.field_image &&
              block.field_image.bundle === 'image' &&
              block.field_image.field_media_image &&
              block.field_image.field_media_image.image.id
              ? block.field_image.field_media_image.image
              : null
          ) as AsyncState<Map<ImageSize<ExtraImages>, Url>>
        )
      ]
    case 'text_with_read_more':
      const imagesTWRM: Array<Pair<number, AsyncState<Map<ImageSize<ExtraImages>, Url>>>> = []
      block.field_read_more
        .filter((frm) => frm.bundle === 'media_nested')
        .forEach((frm) => {
          const ppm = frm as ParsedParagraphMediaNested
          ppm.field_media_item.forEach((fmi) => {
            const imageId = fmi.field_media.bundle === 'image' ? fmi.field_media.field_media_image.image.id : null
            const imageData = fmi.field_media.bundle === 'image' ? fmi.field_media.field_media_image.image : null
            const pair = mk_pair(imageId, loadMedia(imageData) as AsyncState<Map<ImageSize<ExtraImages>, Url>>)
            imagesTWRM.push(pair)
          })
        })
      return imagesTWRM
    default:
      if (get_images_from_custom_block && isCustomBlock(block)) {
        return get_images_from_custom_block(block)
      }
      return []
  }
}
